import React from 'react';
import { Document, Page, View, StyleSheet, Font, Image, Text } from '@react-pdf/renderer';
// @ts-expect-error: no types for qrcode
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';

Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-Regular.ttf', fontWeight: 'normal', fontStyle: 'normal' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-Bold.ttf', fontWeight: 'bold', fontStyle: 'normal' });

const styles = StyleSheet.create({
  page: {
    width: 226.77, // 8cm
    height: 113.39, // 4cm
    padding: 8,
    fontFamily: 'Open Sans',
    backgroundColor: '#fff',
    borderWidth: 3,
    borderColor: '#000',
    borderStyle: 'solid',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  row1: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  colQr: {
    width: 60,
    justifyContent: 'center',
    alignItems: 'center',
  },
  qr: {
    width: 70,
    height: 70,
    marginLeft: 20,
  },
  colInfo: {
    flex: 1,
    justifyContent: 'center',
    paddingLeft: 25,
  },
  assetName: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#222',
    marginBottom: 2,
  },
  assetCode: {
    fontSize: 10,
    color: '#1677ff',
    fontWeight: 'bold',
    marginBottom: 2,
  },
  barcodeContainer: {
    marginTop: 4,
  },
  barcode: {
    width: 120,
    height: 30,
  },
  row2: {
    marginTop: 6,
  },
  companyText: {
    fontSize: 8,
    color: '#888',
    textAlign: 'left',
  },
});

export interface AssetLabelData {
  assetCode: string;
  assetName: string;
  publicUrl: string;
  companyName: string;
  barcodeBase64?: string | null;
  qrCodeBase64?: string | null;
}

interface MultiAssetLabelPDFProps {
  assets: AssetLabelData[];
}

// Helper function để tạo barcode base64
const generateBarcodeBase64 = (value: string): string => {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, value, {
    format: 'CODE128',
    width: 2,
    height: 40,
    fontSize: 12,
    margin: 0,
    displayValue: false,
    background: 'transparent',
    lineColor: '#000',
  });
  return canvas.toDataURL('image/png');
};

// Helper function để cắt ngắn tên tài sản
const truncate = (str: string, max: number) =>
  str.length > max ? str.slice(0, max - 3) + '...' : str;

const MultiAssetLabelPDF: React.FC<MultiAssetLabelPDFProps> = ({ assets }) => {
  // Hooks luôn ở đầu component
  const [qrList, setQrList] = React.useState<(string | null)[]>([]);
  const [barcodeList, setBarcodeList] = React.useState<(string | null)[]>([]);

  // Kiểm tra xem tất cả assets có barcodeBase64 và qrCodeBase64 không
  const allAssetsHaveCodes = assets.every(asset => asset.barcodeBase64 && asset.qrCodeBase64);

  // Nếu chưa có đủ base64 thì tạo QR/barcode cho fallback
  React.useEffect(() => {
    if (!allAssetsHaveCodes) {
      let isMounted = true;
      Promise.all(
        assets.map((a, index) =>
          new Promise<string | null>(resolve => {
            QRCode.toDataURL(a.publicUrl, { width: 128, margin: 0 }, (err: Error | null, url: string) => {
              resolve(!err ? url : null);
            });
          })
        )
      ).then(list => {
        if (isMounted) setQrList(list);
      });
      Promise.all(
        assets.map((a, index) =>
          new Promise<string | null>(resolve => {
            try {
              const barcodeUrl = generateBarcodeBase64(a.assetCode);
              resolve(barcodeUrl);
            } catch (error) {
              resolve(null);
            }
          })
        )
      ).then(list => {
        if (isMounted) setBarcodeList(list);
      });
      return () => { isMounted = false; };
    }
  }, [assets, allAssetsHaveCodes]);

  if (allAssetsHaveCodes) {
    return (
      <Document>
        {assets.map((asset, pageIdx) => (
          <Page key={pageIdx} size={{ width: 226.77, height: 113.39 }} style={styles.page}>
            {/* Row 1 */}
            <View style={styles.row1}>
              {/* Cột 1: QR code */}
              <View style={styles.colQr}>
                {asset.qrCodeBase64 && <Image src={asset.qrCodeBase64} style={styles.qr} />}
              </View>
              {/* Cột2 tin */}
              <View style={styles.colInfo}>
                <Text style={styles.assetName}>{asset.assetName}</Text>
                <Text style={styles.assetCode}>{asset.assetCode}</Text>
                {asset.barcodeBase64 && (
                  <View style={styles.barcodeContainer}>
                    <Image src={asset.barcodeBase64} style={styles.barcode} />
                  </View>
                )}
              </View>
            </View>
            {/* Row 2: Text nhỏ */}
            <View style={styles.row2}>
            </View>
          </Page>
        ))}
      </Document>
    );
  }

  if (qrList.length !== assets.length || barcodeList.length !== assets.length) {
    return null;
  }

  return (
    <Document>
      {assets.map((asset, pageIdx) => {
        const qrDataUrl = qrList[pageIdx];
        const barcodeDataUrl = barcodeList[pageIdx];
        return (
          <Page key={pageIdx} size={{ width: 226.77, height: 113.39 }} style={styles.page}>
            {/* Row 1 */}
            <View style={styles.row1}>
              {/* Cột 1: QR code */}
              <View style={styles.colQr}>
                {qrDataUrl && <Image src={qrDataUrl} style={styles.qr} />}
              </View>
              {/* Cột2 tin */}
              <View style={styles.colInfo}>
                <View style={{ maxWidth: 120 }}>
                  <Text style={styles.assetName} wrap={false}>
                    {truncate(asset.assetName, 12)}
                  </Text>
                </View>
                <Text style={styles.assetCode}>{asset.assetCode}</Text>
                {barcodeDataUrl && (
                  <View style={styles.barcodeContainer}>
                    <Image src={barcodeDataUrl} style={styles.barcode} />
                  </View>
                )}
              </View>
            </View>
            {/* Row 2: Text nhỏ */}
            <View style={styles.row2}>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

export default MultiAssetLabelPDF; 